import { Component } from 'react';
import { concat, from, Subscription } from 'rxjs';
import { tap, debounceTime } from 'rxjs/operators';

import { Commons } from '../../../commons/commons';
import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { LoginService } from '../../../services/login.service';
import { PaymentService } from '../../../services/payments.service';
import type { CheckoutStatus, PlanChangeResult } from '../../../services/payments.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SettingsService } from '../../../services/settings.service';
import { SpinnerService } from '../../../services/spinner.service';
import { UIContext } from '../../../services/ui-context';
import { longDate, money, newAttemptId, planLabel } from '../../portal/payments/checkout-format';
import {
    CARD_DECLINED_TITLE,
    CARD_PROCESSING,
    CURRENT_PLAN,
    downgradeApplied,
    downgradePreview,
    duplicateChargedAndCancelled,
    PAYPAL_PENDING,
    PLAN_CHANGE_RESUMES,
    PLAN_CHANGE_TITLE,
    PLAN_CHANGED_TITLE,
    THREE_DS_FAILED,
    upgradeApplied,
    upgradePreview,
} from '../../portal/payments/checkout-messages';
import { classifyPayError, classifyPlanChangeError, decideGate } from './price-page.gate';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { untilDestroyed } from '../../../utils/until-destroyed';
import LoginComponent from '../login/login.component';
import { TabBody, TabHeader, TabsComponent } from '../tabs/tabs.component';
import pricePageStyles from './price-page.component.module.scss';
import { PlanChangeComponent } from '../../portal/plan-change.component';
import { WindowService } from '../../../services/window.service';
import Link from 'next/link';
import { FormGroup, FormControl, BasicValidators } from '../../../models/forms';
import { RxUtils } from '../../../utils/rx-utils';
declare var Stripe;

const styles = pricePageStyles;

type PaymentMethod = 'creditcard' | 'paypal';
type BillingCycles = 'monthly' | 'yearly';
type PackageTypes = 'basic' | 'pro';
// type PlanId = 'web_premium_monthly' | 'desktop_monthly' | 'web_premium_yearly' | 'desktop_yearly';
type PlanId = 'backer_monthly' | 'backer_yearly' | 'patron_monthly' | 'patron_yearly';
// type PlanName = 'web_premium' | 'desktop';
type PlanName = 'backer' | 'patron';

interface PricePageComponentState {
    activeTab: string;
    paymentMethod: PaymentMethod;
    /** W0-12 (2): `GET /payments/status` — null until loaded or when the flag is off (= today's UI). */
    checkoutStatus: CheckoutStatus | null;
    /** `featureFlags.checkoutStatusGate` (W0-12). `false` until settings arrive = today's behaviour. */
    checkoutStatusGate: boolean;
    /** `featureFlags.payDecideBeforeCharge` (W0-1) — selects the 3DS-failure copy only; the server owns the rest. */
    payDecideBeforeCharge: boolean;
}

/** Unit tests inject stubs here; production never passes it (`@lazyInject` wires the real services). */
export interface PricePageTestServices {
    uiContext?: Partial<UIContext>;
    loginService?: Pick<LoginService, 'isUserLoggedIn' | 'onUserDetailsChanged'>;
    spinnerService?: Pick<SpinnerService, 'runWithLoader'>;
    paymentsService?: Partial<PaymentService>;
    scriptLoader?: Pick<ScriptLoadService, 'loadAllScriptsIfNotLoaded' | 'loadStripe'>;
    windowService?: Pick<WindowService, 'manuallyActivateTriggers'>;
    settingsService?: Pick<SettingsService, 'featureFlags'>;
}

export default class PricePageComponent extends Component<any, PricePageComponentState> {
    readonly PAYPAL_PLAN_BACKER_MONTHLY = Commons.PAYPAL_PLAN_BACKER_MONTHLY;
    readonly PAYPAL_PLAN_BACKER_YEARLY = Commons.PAYPAL_PLAN_BACKER_YEARLY;
    readonly PAYPAL_PLAN_PATRON_MONTHLY = Commons.PAYPAL_PLAN_PATRON_MONTHLY;
    readonly PAYPAL_PLAN_PATRON_YEARLY = Commons.PAYPAL_PLAN_PATRON_YEARLY;

    formGroup: FormGroup;
    formGroupSubcription: Subscription;

    planName: PlanName;
    readablePlanName: string;
    planBillingCycle: BillingCycles;
    readablePlanBillingCycle: string;
    selectedPlanId: PlanId;

    private form: HTMLFormElement;
    private stripe: any;
    example: HTMLElement;
    elements;
    cardElements: any[];

    private elementStyles = {
        base: {
            color: '#3c4257',
            fontWeight: 500,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            fontSize: '15px',
            fontSmoothing: 'antialiased',
            iconColor: '#8898aa',

            ':focus': {
                color: '#3c4257',
            },

            '::placeholder': {
                color: '#aab7c4',
            },

            ':focus::placeholder': {
                color: '#cfd7df',
            },
        },
        invalid: {
            color: '#e23b3b',
            iconColor: '#e23b3b',
            '::placeholder': {
                color: '#f2a4a4',
            },
        },
    };

    private elementClasses = {
        focus: styles.focus,
        empty: styles.empty,
        invalid: styles.invalid,
    };

    get isUserLoggedIn() {
        return this.loginService.isUserLoggedIn;
    }

    @lazyInject(UIContext) private _uiContext: UIContext;
    @lazyInject(LoginService) private _loginService: LoginService;
    @lazyInject(SpinnerService) private _spinnerService: SpinnerService;
    @lazyInject(PaymentService) private _paymentsService: PaymentService;
    @lazyInject(ScriptLoadService) private _scriptLoader: ScriptLoadService;
    @lazyInject(WindowService) private _windowService: WindowService;
    @lazyInject(SettingsService) private _settingsService: SettingsService;

    private get testServices(): PricePageTestServices | undefined { return this.props?.__testServices; }
    private get uiContext() { return (this.testServices?.uiContext ?? this._uiContext) as UIContext; }
    private get loginService() { return (this.testServices?.loginService ?? this._loginService) as LoginService; }
    public get spinnerService() { return (this.testServices?.spinnerService ?? this._spinnerService) as SpinnerService; }
    private get paymentsService() { return (this.testServices?.paymentsService ?? this._paymentsService) as PaymentService; }
    private get scriptLoader() { return (this.testServices?.scriptLoader ?? this._scriptLoader) as ScriptLoadService; }
    private get windowService() { return (this.testServices?.windowService ?? this._windowService) as WindowService; }
    private get settingsService() { return (this.testServices?.settingsService ?? this._settingsService) as SettingsService | undefined; }

    readonly state: Readonly<PricePageComponentState> = {
        activeTab: this.initializePlanDetailsAndReturnBillingCycle(),
        paymentMethod: 'paypal',
        checkoutStatus: null,
        checkoutStatusGate: false,
        payDecideBeforeCharge: false,
    };
    constructor(props) {
        super(props);

    }

    private initializePlanDetailsAndReturnBillingCycle() {
        this.windowService.manuallyActivateTriggers();
        this.updatePlanNameFromLocation();
        this.updateSelectedPlan(this.planName, this.planBillingCycle);
        return this.planBillingCycle;
    }

    private updatePlanNameFromLocation() {
        const pathnames = this.props.path.split('/');
        const id = pathnames.filter(path => path.match(/(year)|(month)/i))[0];
        this.updatePlan(id);
    }

    componentDidMount() {
        // Wave-0 flags (ReplaySubject(1): emits at once when settings are already loaded; `undefined` ≡ false).
        this.settingsService?.featureFlags?.pipe(untilDestroyed(this)).subscribe({
            next: (ff) => {
                const gate = ff?.checkoutStatusGate === true;
                this.setState({ checkoutStatusGate: gate, payDecideBeforeCharge: ff?.payDecideBeforeCharge === true });
                if (gate) this.loadCheckoutStatus();
            },
        });
        this.loginService.onUserDetailsChanged.pipe(untilDestroyed(this)).subscribe({
            next: () => { if (this.state.checkoutStatusGate) this.loadCheckoutStatus(); },
            error: () => undefined,
        });
        concat(
            from(this.scriptLoader.loadAllScriptsIfNotLoaded(true)),
            from(this.scriptLoader.loadStripe()),
            from(ScriptLoadService.loadPayPal()),
        ).pipe(
            untilDestroyed(this),
        ).subscribe({
            next: () => {
                this.loginService.onUserDetailsChanged.pipe(
                    untilDestroyed(this),
                ).subscribe({
                    next: (userDetails) => {
                        if (this.loginService.isUserLoggedIn) {
                            this.setState({
                                activeTab: this.planBillingCycle,
                            });

                            setTimeout(() => {
                                this.stripe = Stripe(Commons.STRIPE_PUBLIC_KEY);
                                // Stripe card elements only exist in the DOM when the
                                // credit-card tab is active. Defer init to updatePaymentMethod
                                // if we're on PayPal. Without this, stripe.mount('#example3-...')
                                // throws 'The selector applies to no DOM elements'.
                                if (this.state.paymentMethod === 'creditcard') {
                                    this.initializeCardElement();
                                }
                            }, 1000);
                        }
                    },
                    error: (err) => {
                        this.uiContext.showErrorDialog({
                            title: 'Error',
                            text: 'An error occurred. Please reload the page.'
                        });
                    }
                });
                this.reloadFormGroup();
            }
        });
    }

    /**
     * W0-12 (2): ask the server what this user may buy. A failed call leaves `checkoutStatus` null → today's
     * buttons (the server still enforces the policy on create; the gate only avoids offering what it would refuse).
     */
    private statusRequest = 0;
    private loadCheckoutStatus() {
        if (!this.loginService.isUserLoggedIn || typeof this.paymentsService?.getStatus !== 'function') return;
        const seq = ++this.statusRequest;
        try {
            this.paymentsService.getStatus().pipe(untilDestroyed(this)).subscribe({
                next: (status) => { if (seq === this.statusRequest) this.setState({ checkoutStatus: status ?? null }); },
                error: (err) => {
                    // eslint-disable-next-line no-console
                    console.error('[checkout] GET /payments/status failed', err?.message);
                    if (seq === this.statusRequest) this.setState({ checkoutStatus: null });
                },
            });
        } catch (err: any) {
            // eslint-disable-next-line no-console
            console.error('[checkout] GET /payments/status threw', err?.message);
        }
    }

    /**
     * W0-4 UI: preview first (`preview: true` charges nothing), confirm with the exact amounts, then apply. Every
     * refusal (PayPal subscriber, flag off, 402 on the proration invoice) is the server's §0 text via handleError.
     */
    public async changePlan() {
        const planName = this.selectedPlanId;
        const label = planLabel(planName);
        let preview: PlanChangeResult | undefined;
        try {
            await this.spinnerService.runWithLoader(async () => {
                preview = await this.paymentsService.changePlan(planName, true).toPromise();
            }, 'Checking your plan change…');
        } catch (err) {
            if (this.handlePlanChangeRefusal(err)) return;
            this.uiContext.handleError(err as Error);
            return;
        }
        if (!preview) return;
        const text = this.planChangeText(preview, label, false);
        const prorationDate = preview.prorationDate;
        this.uiContext.showConfirmationWarningDialog(PLAN_CHANGE_TITLE, text, () => this.applyPlanChange(planName, label, prorationDate), true, 'Keep my plan', 'Switch plan');
    }

    private async applyPlanChange(planName: string, label: string, prorationDate?: number) {
        try {
            let result: PlanChangeResult | undefined;
            // CR-L6-2 (W0-1 rule): one attemptId per confirm click; the server's idempotency key is plan-change-<attemptId>.
            const attemptId = newAttemptId();
            await this.spinnerService.runWithLoader(async () => {
                result = await this.paymentsService.changePlan(planName, false, attemptId, prorationDate).toPromise();
            }, 'Switching your plan…');
            this.uiContext.showSuccessDialog(PLAN_CHANGED_TITLE, this.planChangeText(result as PlanChangeResult, label, true));
            setTimeout(() => { location.href = '/portal'; }, 3000);
        } catch (err) {
            if (this.handlePlanChangeRefusal(err)) return;
            this.uiContext.handleError(err as Error);
        }
    }

    /**
     * CR-F1-1 (QA-4): a refused change-plan carries `code`. `ALREADY_ON_PLAN` / `PAYMENT_UPDATE_REQUIRED` — the server is
     * the truth about what this user may buy: show its sentence once and re-GET `/payments/status` so the gate re-renders
     * ("Current plan" / the "Update your card" link). `CARD_DECLINED` — the checkout's own declined dialog. Anything else
     * (no code, PLAN_CHANGE_UNAVAILABLE, UNKNOWN_PLAN, network) → false, the caller's handleError shows the message as today.
     */
    private handlePlanChangeRefusal(err: unknown): boolean {
        const c = classifyPlanChangeError(err);
        switch (c.kind) {
            case 'already-on-plan':
            case 'payment-update-required':
                this.uiContext.showErrorDialog({ title: PLAN_CHANGE_TITLE, text: c.message });
                this.loadCheckoutStatus();
                return true;
            case 'declined':
                this.uiContext.showErrorDialog({ title: CARD_DECLINED_TITLE, text: c.message });
                return true;
            default:
                return false;
        }
    }

    private planChangeText(r: PlanChangeResult, label: string, applied: boolean): string {
        // CR-L6-2: the server renders the §0 sentence itself (amounts from the real preview/invoice); it wins when present.
        if (typeof r?.message === 'string' && r.message.trim()) return r.message;
        const cur = r?.currency || 'usd';
        const next = money(r?.nextChargeCents, cur) || '—';
        const nextDate = longDate(r?.nextChargeDate) || 'your next billing date';
        const credit = r?.creditCents || 0;
        const charged = r?.chargedTodayCents || 0;
        const body = credit > 0 && charged <= 0
            ? (applied ? downgradeApplied : downgradePreview)(label, money(credit, cur), next, nextDate)
            : (applied ? upgradeApplied : upgradePreview)(label, money(charged, cur) || money(0, cur), next, nextDate);
        return r?.resumesSubscription ? `${body} ${PLAN_CHANGE_RESUMES}` : body;
    }

    /**
     * W0-12 (2): what the payment section shows for this user and plan; `null` → today's payment tabs.
     * With the flag off `checkoutStatus` is never loaded, so this returns null byte for byte.
     */
    private renderStatusGate() {
        if (!this.state.checkoutStatusGate || !this.state.checkoutStatus) return null;
        const d = decideGate(this.state.checkoutStatus, this.selectedPlanId);
        switch (d.kind) {
            case 'buttons':
                return null;
            case 'current-plan':
                return <div className="mt-3" data-testid="gate-current-plan">
                    <button type="button" className="btn btn-secondary w-100" disabled aria-disabled="true">{CURRENT_PLAN}</button>
                </div>;
            case 'change-plan':
                return <div className="mt-3" data-testid="gate-change-plan">
                    <button type="button" className="btn btn-primary w-100" onClick={() => this.changePlan()}>{d.label}</button>
                </div>;
            case 'paypal-support':
                return <div className="alert alert-info mt-3" role="status" data-testid="gate-paypal-support">{d.message}</div>;
            case 'cross-gateway':
                return <div className="alert alert-warning mt-3" role="status" data-testid="gate-cross-gateway">{d.message}</div>;
            case 'payment-update':
                return <div className="alert alert-warning mt-3" role="status" data-testid="gate-payment-update">
                    <div>{d.message}</div>
                    <a className="btn btn-primary mt-2" href={d.href} data-testid="gate-update-card-link">{d.linkLabel}</a>
                </div>;
            case 'in-progress':
                return <div className="alert alert-info mt-3" role="status" data-testid="gate-in-progress">{d.message}</div>;
            case 'finishing-paypal':
                return <div className="alert alert-info mt-3" role="status" data-testid="gate-finishing-paypal">{d.message}</div>;
            default:
                return null;
        }
    }

    reloadFormGroup() {
        RxUtils.safeCloseSubscriber(this.formGroupSubcription);
        this.formGroup = new FormGroup({
            tosAccepted: new FormControl(false, { required: BasicValidators.required, requiredTruthy: BasicValidators.requiredTruthy }),
        });
        this.formGroupSubcription = this.formGroup.valueChanges.pipe(
            debounceTime(30),
        ).subscribe({
            next: () => {
                setTimeout(() => this.forceUpdate(), 10);
            }
        });
    }

    handlePlanChangedEvent(billingCycle: BillingCycles) {
        this.updateSelectedPlan(this.planName, billingCycle);
        this.updatePlan(this.selectedPlanId);
        this.setState({
            activeTab: this.planBillingCycle,
        });
    }

    private updateSelectedPlan(planName: string, billingCycle: string) {
        const selectedPlanId = (planName + '_' + billingCycle) as PlanId;
        if (this.isPlanId(selectedPlanId)) {
            this.selectedPlanId = selectedPlanId;
        } else {
            if (BrowserUtils.hasWindow()) {
                throw new Error(`Unrecognized plan id, ${selectedPlanId}`);
            }
        }
    }

    updatePaymentMethod(paymentMethod: PaymentMethod) {
        this.reloadFormGroup();
        this.setState({
            paymentMethod,
        });
        // Stripe elements mount into DOM nodes that only exist when the
        // credit-card tab is active. Initialize after React flushes the
        // new tab body so the elements are present.
        if (paymentMethod === 'creditcard' && this.stripe && !this.cardElements) {
            setTimeout(() => this.initializeCardElement(), 0);
        }
    }

    public async submitForPayPal(subscriptionId: string) {
        // §4.2 UX: spinner during backend call, toast on 5xx.
        //   - Success path: redirect to /portal (3s delay to let the toast read).
        //   - 5xx / network: show the "payment received, we'll reconcile" toast
        //     so the user isn't left worried. The webhook + cron back-fill
        //     ensures the account updates automatically within a minute.
        //   - 4xx (A1 ownership, plan mismatch, E7 Stripe conflict): surfaces
        //     via handleError's alert dialog — ClientError carries the structured
        //     response body so the user sees the actual reason, not a generic
        //     "Something went wrong".
        await this.spinnerService.runWithLoader(async () => {
            try {
                await this.paymentsService.payPayPal(subscriptionId, this.selectedPlanId)
                    .pipe(
                        tap((response: any) => {
                            // W0-2 (PA-03/PA-14 UI half): `{state:'pending'}` = approved at PayPal, not yet
                            // ACTIVATED; the webhook finishes it. Say so and stay — /portal would show "Free".
                            if (response?.state === 'pending') {
                                // MON-13 (2) (CR-F2-6): the server's sentence wins when present (PA-10 "starts on <date>");
                                // the mirrored constant is the fallback for a build that sends none.
                                const text = typeof response?.message === 'string' && response.message.trim() ? response.message : PAYPAL_PENDING;
                                this.uiContext.showSuccessDialog('PayPal approved your subscription', text);
                                return;
                            }
                            setTimeout(() => {
                                location.href = '/portal';
                            }, 3000);
                        }),
                    )
                    .toPromise();
            } catch (e) {
                const name = (e as Error)?.name;
                if (name === 'ServiceError' || name === 'UnknownHTTPError') {
                    this.uiContext.errorToast(
                        'Payment received — syncing your account',
                        'We received your payment but had trouble updating your account. We\'ll reconcile automatically within a minute. If your plan doesn\'t update, email support@xconvert.com.',
                    );
                } else {
                    this.uiContext.handleError(e as Error);
                }
            }
        }, 'Completing your subscription…');
    }

    public async submitFormForStripe() {
        await this.spinnerService.runWithLoader(async () => {
            // Trigger HTML5 validation UI on the form if any of the inputs fail
            // validation.
            let plainInputsValid = true;
            Array.prototype.forEach.call(this.form.querySelectorAll('input'), (
                input,
            ) => {
                if (input.checkValidity && !input.checkValidity()) {
                    plainInputsValid = false;
                    return;
                }
            });
            if (!plainInputsValid) {
                this.triggerBrowserValidation();
                return;
            }

            // Gather additional customer data we may have collected in our form.
            const name = this.form.querySelector('#example3-name') as HTMLInputElement;
            const address1 = this.form.querySelector('#example3-address') as HTMLInputElement;
            const city = this.form.querySelector('#example3-city') as HTMLInputElement;
            const state = this.form.querySelector('#example3-state') as HTMLInputElement;
            const zip = this.form.querySelector('#example3-zip') as HTMLInputElement;
            const additionalData = {
                name: name ? name.value : undefined,
                address_line1: address1 ? address1.value : undefined,
                address_city: city ? city.value : undefined,
                address_state: state ? state.value : undefined,
                address_zip: zip ? zip.value : undefined,
            };

            // Create a PaymentMethod (post-Stripe-upgrade path). `createToken` is
            // deprecated and the backend rejects `{stripeToken, ...}` bodies with 409.
            // billing_details carry the additional customer data we collected.
            const result = await this.stripe.createPaymentMethod({
                type: 'card',
                card: this.cardElements[0],
                billing_details: {
                    name: additionalData.name,
                    address: {
                        line1: additionalData.address_line1,
                        city: additionalData.address_city,
                        state: additionalData.address_state,
                        postal_code: additionalData.address_zip,
                    },
                },
            });

            // Stop loading!
            this.example.classList.remove('submitting');

            if (result.paymentMethod) {
                // W0-1 step 2: one UUID per submit → Stripe idempotency key `pay-{attemptId}` on the server.
                const attemptId = newAttemptId();
                try {
                    const response: any = await this.paymentsService
                        .payStripe(result.paymentMethod.id, this.selectedPlanId, attemptId)
                        .toPromise();

                    // W0-1 step 3: PI `processing` → `{pending}`; confirmStripe/webhook finish it. Nothing to confirm here.
                    if (response?.pending) {
                        this.uiContext.showSuccessDialog('Payment processing', CARD_PROCESSING);
                        return;
                    }

                    // SCA / 3DS path — backend returned `requires_action` with a
                    // client_secret from latest_invoice.confirmation_secret. Confirm it
                    // client-side then hit /payments/confirm-stripe to finalize state.
                    if (response?.requires_action && response?.clientSecret) {
                        // W0-1 step 7: under `payDecideBeforeCharge` the server returns `paymentMethodId` and the
                        // PI is confirmed WITH it (CA-04: a PM-less confirm fails with "missing a payment method").
                        // Today's response carries no `paymentMethodId` → today's one-argument call.
                        const confirmResult = response?.paymentMethodId
                            ? await this.stripe.confirmCardPayment(response.clientSecret, { payment_method: response.paymentMethodId })
                            : await this.stripe.confirmCardPayment(response.clientSecret);
                        if (confirmResult.error) {
                            this.enableInputs();
                            // W0-1 step 7: "no charge was made" is true only when the server decided before charging.
                            this.uiContext.showErrorDialog(this.state.payDecideBeforeCharge
                                ? { title: 'Authentication failed', text: THREE_DS_FAILED }
                                : { title: 'Authentication failed', text: confirmResult.error.message || 'Please try again.' });
                            return;
                        }
                        await this.paymentsService.confirmStripe(response.subscriptionId).toPromise();
                    }

                    this.uiContext.showSuccessDialog('Success!', 'Payment successful! Redirecting in a few seconds...');
                    setTimeout(() => {
                        location.href = '/portal';
                    }, 3000);
                } catch (err) {
                    const c = classifyPayError(err);
                    if (c.kind === 'declined') {
                        // W0-1 step 3/7 (CA-04, CA-32): 402 = declined before anything was persisted; own dialog.
                        this.enableInputs();
                        this.uiContext.showErrorDialog({ title: CARD_DECLINED_TITLE, text: c.message });
                        return;
                    }
                    if (c.kind === 'duplicate-charged') {
                        // W0-10 (B1 409 until W0-1 removes the path): the card WAS charged — say so, with the
                        // attempt id as the support reference (it is logged server-side with the request).
                        this.uiContext.showErrorDialog({
                            title: 'Payment needs attention',
                            text: duplicateChargedAndCancelled(this.selectedPlanPrice(), attemptId),
                        });
                        return;
                    }
                    this.uiContext.handleError(err);
                }
            } else {
                // Otherwise, un-disable inputs.
                this.enableInputs();
                const errMsg = result.error?.message ?? 'Please check your details and try again.';
                this.uiContext.showErrorDialog({ title: 'Payment failed', text: errMsg });
            }
        });
    }

    /** The page's own price for the selected plan ("$9.99"), used only for the W0-10 B1 text. */
    private selectedPlanPrice(): string {
        const yearly = this.planBillingCycle === 'yearly';
        return this.planName === 'patron' ? (yearly ? '$99.99' : '$9.99') : (yearly ? '$49.99' : '$4.99');
    }

    private updatePlan(planId: string) {
        const _planName = planId.substring(0, planId.lastIndexOf('_')) as PlanName;
        if (this.isPlanName(_planName)) {
            this.planName = _planName;
        } else {
            throw new Error(`Unrecognized Plan Name, ${_planName}`);
        }
        this.readablePlanName = this.planName.match(/patron/) ? 'Pro' : 'Basic';
        const billingCycle = planId.substring(planId.lastIndexOf('_') + 1);
        if (billingCycle === 'monthly' || billingCycle === 'yearly') {
            this.planBillingCycle = billingCycle;
        } else {
            throw new Error(`Unrecognized billing cycle, ${billingCycle}`);
        }
        this.readablePlanBillingCycle = this.planBillingCycle.replace(/_/i, ' ');
    }

    private enableInputs() {
        Array.prototype.forEach.call(
            this.form.querySelectorAll(
                'input[type=\'text\'], input[type=\'email\'], input[type=\'tel\']',
            ),
            (input) => {
                input.removeAttribute('disabled');
            },
        );
    }

    private triggerBrowserValidation() {
        // The only way to trigger HTML5 this.form validation UI is to fake a user submit
        // event.
        const submit = document.createElement('input');
        submit.type = 'submit';
        submit.style.display = 'none';
        this.form.appendChild(submit);
        submit.click();
        submit.remove();
    }

    private initializeCardElement() {
        this.elements = this.stripe.elements({
            fonts: [
                {
                    cssSrc: 'https://fonts.googleapis.com/css?family=Quicksand',
                },
            ],
        });

        const cardNumber = this.elements.create('cardNumber', {
            style: this.elementStyles,
            classes: this.elementClasses,
        });
        cardNumber.mount('#example3-card-number');

        const cardExpiry = this.elements.create('cardExpiry', {
            style: this.elementStyles,
            classes: this.elementClasses,
        });
        cardExpiry.mount('#example3-card-expiry');

        const cardCvc = this.elements.create('cardCvc', {
            style: this.elementStyles,
            classes: this.elementClasses,
        });
        cardCvc.mount('#example3-card-cvc');

        this.cardElements = [cardNumber, cardExpiry, cardCvc];
        this.registerElements();
    }

    private registerElements() {
        const formClass = 'example-3';
        this.example = document.getElementById(formClass);
        this.form = this.example.querySelector('form') as HTMLFormElement;
    }

    private getPlanIds(): PlanId[] {
        return ['backer_monthly', 'backer_yearly', 'patron_monthly', 'patron_yearly'];
    }

    private isPlanId(id: string) {
        return 'backer_monthly' === id ||
            'patron_monthly' === id ||
            'backer_yearly' === id ||
            'patron_yearly' === id;
    }

    private getPlanNames(): PlanName[] {
        return ['backer', 'patron'];
    }

    private isPlanName(planName: string) {
        return 'backer' === planName ||
            'patron' === planName;
    }

    getPackageDetails() {
        return <div className="col-sm-12 col-md-5 col-lg-4 mt-3">
            <div className={`${styles.features}`}>
                <h5 className="text-center font-weight-bold mt-2">
                    {this.readablePlanName === 'patron' ? 'Pro' : 'Basic'}</h5>
                <hr />
                {
                    this.readablePlanName === 'patron' && <div>
                        <ul className="text-left">
                            <li className={`${styles['check']}`}><b>No ads — nothing between you and your file</b></li>
                            <li className={`${styles['check']}`}>Fast cloud upload</li>
                            <li className={`${styles['check']}`}>4 GB file size</li>
                            <li className={`${styles['check']}`}>6 hr / task process time</li>
                            <li className={`${styles['check']}`}>25 Concurrent non-video tasks</li>
                            <li className={`${styles['check']}`}>8 Concurrent video tasks</li>
                            <li className={`${styles['check']}`}>400 video conversions per day</li>
                            <li className={`${styles['check']}`}>24 hours of processing time per day</li>
                            <li className={`${styles['check']}`}>Premium encoders</li>
                        </ul>
                        <div className="mt-1">
                            <small>* <a href="https://cloud.google.com/storage/">GCP low latency object
                                storage</a> used for files smaller than 100 MB.</small>
                        </div>
                    </div>
                }
                {
                    this.readablePlanName !== 'patron' && <div>
                        <ul className="text-left">
                            <li className={`${styles['check']}`}><b>No ads — nothing between you and your file</b></li>
                            <li className={`${styles['check']}`}>1 GB file size</li>
                            <li className={`${styles['check']}`}>2 hr / task process time</li>
                            <li className={`${styles['check']}`}>10 Concurrent non-video tasks</li>
                            <li className={`${styles['check']}`}>4 Concurrent video tasks</li>
                            <li className={`${styles['check']}`}>200 video conversions per day</li>
                            <li className={`${styles['check']}`}>8 hours of processing time per day</li>
                            <li className={`${styles['check']}`}>Advanced encoders</li>
                        </ul>
                    </div>
                }
                <hr />
                <span className="font-weight-bold mb-3"> <b>Cancel anytime.</b></span>
            </div>
        </div>
    }

    getBillingCycleHTML() {
        return <TabsComponent activeTabName={this.state.activeTab} onTabChange={(tabname) => this.handlePlanChangedEvent(tabname as any)}>
            <TabHeader type="header" headerFor="monthly">
                MONTHLY
            </TabHeader>
            <TabHeader type="header" headerFor="yearly">
                YEARLY
            </TabHeader>
            <TabBody name="monthly" type="body">
                <table className="w-100 p-2">
                    <tbody>
                        <tr>
                            <td>Price per month</td>
                            <td className="text-right">
                                {this.planName !== 'patron' ? '4.99' : '9.99'} USD/month
                            </td>
                        </tr>
                        <tr>
                            <td>Billed monthly</td>
                            <td className="text-right">{this.planName !== 'patron' ? '4.99' : '9.99'} USD</td>
                        </tr>
                    </tbody>
                </table>
            </TabBody>
            <TabBody name="yearly" type="body">
                <table className="w-100 p-2">
                    <tbody>
                        <tr>
                            <td>Price per month</td>
                            <td className="text-right"> {this.planName !== 'patron' ? '4.17' : '8.33'} USD/month</td>
                        </tr>
                        <tr>
                            <td>Billed yearly</td>
                            <td className="text-right"> {this.planName !== 'patron' ? '49.99' : '99.99'} USD</td>
                        </tr>
                    </tbody>
                </table>
            </TabBody>
        </TabsComponent>
    }

    /**
     * F3: render all 4 PayPal plan containers upfront; switch visibility via
     * CSS. Mounting/unmounting `<PayPalComponent>` on cycle change caused a
     * flicker because each mount re-runs `paypal.Buttons().render(...)` (see
     * paypal-sdk-upgrade.md §4.2). Keeping them all mounted means the card-
     * button eligibility check and DOM insertion happen once per page load.
     *
     * The `paymentMethod === 'paypal'` guard stays — when the user is on the
     * Credit Card tab we skip rendering entirely. Switching between `paypal`
     * and `creditcard` tabs is rare enough that the resulting mount cost is
     * acceptable.
     */
    paypalButton() {
        if (this.state.paymentMethod !== 'paypal') return null;
        // §4.2 F9: PlanChangeComponent is a superset of PayPalComponent — when
        // the user already has an active PayPal sub on a different plan, it
        // shows a "Change plan" confirmation modal first, then swaps to the
        // usual two-button render. Anonymous users + users with no/matching
        // sub see the buttons immediately.
        const plans: Array<{ id: PlanId; planId: string; name: string }> = [
            { id: 'backer_monthly', planId: Commons.PAYPAL_PLAN_BACKER_MONTHLY, name: 'Basic Monthly' },
            { id: 'backer_yearly', planId: Commons.PAYPAL_PLAN_BACKER_YEARLY, name: 'Basic Yearly' },
            { id: 'patron_monthly', planId: Commons.PAYPAL_PLAN_PATRON_MONTHLY, name: 'Pro Monthly' },
            { id: 'patron_yearly', planId: Commons.PAYPAL_PLAN_PATRON_YEARLY, name: 'Pro Yearly' },
        ];
        return <div>
            {plans.map(p =>
                <div key={p.id} style={{ display: this.selectedPlanId === p.id ? 'block' : 'none' }}>
                    <PlanChangeComponent
                        targetPlanId={p.planId}
                        targetPlanName={p.name}
                        planName={p.id}
                        skipSupersedeCheck={this.state.checkoutStatusGate}
                        onCreateFailed={(err) => this.uiContext.handleError(err)}
                        completed={($event) => this.submitForPayPal($event)} />
                </div>
            )}
        </div>;
    }

    handleLoginSuccess() {
        console.log('Not handled');
    }

    isValid() {
        return this.formGroup?.valid;
    }

    getTOSAcceptFormControl() {
        return <div className="mt-3 mb-3" >
            <div className="custom-control custom-checkbox">
                <input type="checkbox" className="custom-control-input" id="customCheck1"
                    onChange={(e: any) => this.formGroup.controls['tosAccepted'].setValue(e.target.checked)}
                />
                <label className="custom-control-label" htmlFor="customCheck1">
                    &nbsp;I agreed to <Link href="/terms-of-service" prefetch={false} target="_blank">
                        Terms of Service
                    </Link>
                </label>
            </div>
        </div>
    }


    getPaymentHTML() {
        // W0-12 (2): the server's verdict replaces the payment tabs when a purchase would be refused or is a change.
        const gate = this.renderStatusGate();
        if (gate) return gate;
        return <TabsComponent activeTabName={this.state.paymentMethod} onTabChange={(tabname) => this.updatePaymentMethod(tabname as any)}>
            <TabHeader type="header" headerFor="paypal">
                <span>PayPal</span>
            </TabHeader>
            <TabHeader type="header" headerFor="creditcard">
                <span>💳 Credit Card</span>
            </TabHeader>
            <TabBody name="paypal" type="body">
                <div className="alert alert-info mt-3 text-center">
                    You will be redirected to PayPal to complete your secure purchase.
                </div>
                <div className="text-center">
                    {
                        this.paypalButton()
                    }
                    <div className={styles['secure-badge']}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                        Secure PayPal Checkout
                    </div>
                </div>
            </TabBody>
            <TabBody name="creditcard" type="body">
                <div id="example-3" className={`mt-3 ${styles['payment-form']}`}>
                    <form>
                        <div className="">
                            <span className={styles['label-text']}>Card details</span>
                            <div id="example3-card-number" className={styles['field']}></div>
                            <div className="d-flex gap-2">
                                <div id="example3-card-expiry" className={`${styles['field']} flex-grow-1`}></div>
                                <div id="example3-card-cvc" className={`${styles['field']} flex-grow-1`}></div>
                            </div>
                        </div>
                    </form>
                </div>
                {
                    this.getTOSAcceptFormControl()
                }
                <div className="">
                    <button type="button" className={styles['pay-button']} disabled={!this.isValid()}
                        onClick={() => this.submitFormForStripe()}>
                        {/* Lock Icon */}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                        Pay Securely
                    </button>
                    <div className={styles['secure-badge']}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                        TLS Encrypted Transaction
                    </div>
                </div>
            </TabBody>
        </TabsComponent>
    }

    render() {
        return <div className={styles['wrapper']}>
            <div className={`${styles['main-card']} ${styles['price-page']}`}>
                <div className={`row ${styles['checkout-wrapper']}`}>

                    {/* Left Column: Checkout Form */}
                    <div className={`col-lg-7 p-0`}>
                        <div className={styles['checkout-form-column']}>
                            <h2>Checkout</h2>
                            <p className="text-secondary mb-4">Complete your subscription to XConvert {this.readablePlanName}</p>

                            {!this.isUserLoggedIn && (
                                <div className="mb-4 text-center">
                                    <p className="mb-3 text-secondary">Please log in to continue</p>
                                    <LoginComponent mode={'login'} registerEnabled={true} passwordResetEnabled={false} submitBtnText={'Login to Continue'}
                                        onSuccess={() => this.handleLoginSuccess()}>
                                    </LoginComponent>
                                </div>
                            )}

                            {this.isUserLoggedIn && (
                                <>
                                    <span className={styles['section-title']}>1. Select Billing Cycle</span>
                                    <div className="mb-4">
                                        {this.getBillingCycleHTML()}
                                    </div>

                                    <span className={styles['section-title']}>2. Payment Details</span>
                                    <div>
                                        {this.getPaymentHTML()}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Order Summary */}
                    <div className={`col-lg-5 p-0`}>
                        <div className={styles['summary-column']}>
                            <h5>Order Summary</h5>
                            <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                                <span className="font-weight-bold text-dark">{(this.readablePlanName === 'patron' || this.readablePlanName === 'Pro') ? 'Pro Plan' : 'Basic Plan'}</span>
                                <span className="badge badge-light text-uppercase p-2">{this.planBillingCycle}</span>
                            </div>

                            {/* Feature List */}
                            <div className={styles['summary-card']}>
                                {(this.readablePlanName === 'patron' || this.readablePlanName === 'Pro') ? (
                                    <ul>
                                        <li><b>No ads — nothing between you and your file</b></li>
                                        <li>Fast cloud upload (GCP)</li>
                                        <li>4 GB file size limit</li>
                                        <li>6 hr / task process time</li>
                                        <li>25 Concurrent tasks</li>
                                        <li>8 Concurrent video tasks</li>
                                        <li>400 video conversions / day</li>
                                        <li>24 h processing time / day</li>
                                        <li>Premium encoders</li>
                                    </ul>
                                ) : (
                                    <ul>
                                        <li><b>No ads — nothing between you and your file</b></li>
                                        <li>1 GB file size limit</li>
                                        <li>2 hr / task process time</li>
                                        <li>10 Concurrent tasks</li>
                                        <li>4 Concurrent video tasks</li>
                                        <li>200 video conversions / day</li>
                                        <li>8 h processing time / day</li>
                                        <li>Advanced encoders</li>
                                    </ul>
                                )}
                            </div>

                            <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center">
                                <span className="h5 mb-0 text-dark">Total</span>
                                <div className="text-right">
                                    <span className="h4 mb-0 text-primary font-weight-bold">
                                        {this.planBillingCycle === 'monthly'
                                            ? (this.planName !== 'patron' ? '$4.99' : '$9.99')
                                            : (this.planName !== 'patron' ? '$49.99' : '$99.99')
                                        }
                                    </span>
                                    <small className="text-muted ml-1">/{this.planBillingCycle === 'monthly' ? 'mo' : 'yr'}</small>
                                </div>
                            </div>

                            <span className={styles['cancel-text']}>
                                Cancel anytime.
                            </span>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    }
}
